/**
 * EditImageSheet.jsx — "Edit with words" for any image in the chat thread.
 *
 * On open we fetch personalized suggestions from /create/edit-suggestions
 * (Rammy peeks at the user's Memory Garden). The user can click a suggestion
 * chip to autofill the prompt, tweak it, or type their own — then submit
 * to /create/edit which returns the newly edited PNG.
 */
import { useEffect, useRef, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Strip the "data:...;base64," prefix if present — the backend accepts both,
// but sending raw base64 keeps request bodies a touch smaller.
const stripDataUrl = (s) => (typeof s === "string" && s.startsWith("data:") ? s.split(",", 2)[1] : s);
// Best-effort mime detection from a data URL prefix.
const detectMime = (s) => {
  if (typeof s !== "string" || !s.startsWith("data:")) return "image/png";
  const m = s.match(/^data:([^;]+);/);
  return m ? m[1] : "image/png";
};

const blobToBase64 = (blob) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onerror = rej;
  r.onload = () => res(String(r.result).split(",")[1]);
  r.readAsDataURL(blob);
});

/** Generated images are stored files now, so the source may be a URL. */
const resolveImage = async (src) => {
  if (typeof src !== "string") return { b64: "", mime: "image/png" };
  if (src.startsWith("data:")) return { b64: stripDataUrl(src), mime: detectMime(src) };
  const r = await fetch(src, { credentials: "include" });
  const blob = await r.blob();
  return { b64: await blobToBase64(blob), mime: blob.type || "image/png" };
};

const MAX_REFS = 3;

export default function EditImageSheet({ imageDataUrl, onClose, onEdited }) {
  const [suggestions, setSuggestions] = useState([]);
  const [hasMemoryContext, setHasMemoryContext] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [activeChip, setActiveChip] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [base, setBase] = useState({ b64: "", mime: "image/png" });
  const [refs, setRefs] = useState([]);   // [{ b64, mime, preview }]
  const rootRef = useRef(null);
  const textareaRef = useRef(null);
  const refInputRef = useRef(null);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  // Fetch personalized suggestions on open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSuggest(true);
      try {
        const resolved = await resolveImage(imageDataUrl);
        if (cancelled) return;
        setBase(resolved);
        const { data } = await api.post("/create/edit-suggestions", {
          image_base64: resolved.b64,
          mime: resolved.mime,
        });
        if (cancelled) return;
        setSuggestions(data?.suggestions || []);
        setHasMemoryContext(!!data?.has_memory_context);
      } catch (e) {
        console.warn("edit-suggestions failed:", e);
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoadingSuggest(false);
      }
    })();
    return () => { cancelled = true; };
  }, [imageDataUrl]);

  const addRefs = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, MAX_REFS - refs.length);
    if (!files.length) return;
    const next = [];
    for (const f of files) {
      if (!f.type?.startsWith("image/")) { toast.error(`${f.name}: not an image.`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8 MB.`); continue; }
      next.push({ b64: await blobToBase64(f), mime: f.type, preview: URL.createObjectURL(f) });
    }
    if (next.length) setRefs(r => [...r, ...next]);
  };

  const pickChip = (i) => {
    setActiveChip(i);
    setPrompt(suggestions[i]?.prompt || "");
    // Let the user tweak the prompt right away.
    setTimeout(() => textareaRef.current?.focus(), 30);
  };

  const submit = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    setBusy(true);
    try {
      const img = base.b64 ? base : await resolveImage(imageDataUrl);
      const { data } = await api.post("/create/edit", {
        image_base64: img.b64,
        mime: img.mime,
        prompt: p,
        reference_images: refs.map(r => r.b64),
      });
      const src = data.url
        ? `${process.env.REACT_APP_BACKEND_URL || ""}${data.url}`
        : `data:${data.mime};base64,${data.image_base64}`;
      try { onEdited?.(src, p); } catch (_) { /* parent handler shouldn't throw */ }
      onClose?.();
    } catch (e) {
      console.warn("edit failed:", e);
      toast.error("Edit didn't take — try a different phrase?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[var(--rm-ink-900)]/45 backdrop-blur-sm"
      onPointerDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
      data-testid="edit-image-sheet"
    >
      <div
        ref={rootRef}
        className="relative w-full sm:max-w-2xl bg-[var(--rm-surface)] sm:rounded-3xl shadow-2xl border-t sm:border border-black/10 max-h-[92vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-3 border-b border-[var(--rammy-border)]">
          <div className="w-10 h-10 rounded-xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center">
            <L.Wand2 size={18}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[15px]">Edit with words</div>
            <div className="text-[12px] text-[var(--rammy-text-muted)]">
              {hasMemoryContext
                ? "Rammy peeked at your Memory Garden for ideas."
                : "Describe the change — or pick one of Rammy's ideas."}
            </div>
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose?.()}
            className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[var(--rammy-text-muted)]"
            aria-label="Close"
            data-testid="edit-close"
            disabled={busy}
          >
            <L.X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto subtle-scroll px-5 py-4 space-y-4">
          {/* Preview */}
          <div className="flex justify-center">
            <img
              src={imageDataUrl}
              alt="Editing preview"
              className="max-h-56 rounded-2xl border border-black/10 object-contain"
              data-testid="edit-preview"
            />
          </div>

          {/* Reference pictures — bring another photo into the edit */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] mb-2 flex items-center gap-2">
              <L.Images size={11} className="text-[var(--rammy-cyan-dark)]"/>
              Bring in another picture
              <span className="normal-case tracking-normal font-normal opacity-70">
                optional · up to {MAX_REFS}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {refs.map((r, i) => (
                <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-[var(--rammy-border)]">
                  <img src={r.preview} alt={`Reference ${i + 1}`} className="w-full h-full object-cover"/>
                  <button
                    type="button"
                    onClick={() => setRefs(x => x.filter((_, k) => k !== i))}
                    disabled={busy}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                    aria-label="Remove reference"
                    data-testid={`edit-ref-remove-${i}`}
                  >
                    <L.X size={11}/>
                  </button>
                </div>
              ))}
              {refs.length < MAX_REFS && (
                <button
                  type="button"
                  onClick={() => refInputRef.current?.click()}
                  disabled={busy}
                  className="w-16 h-16 rounded-xl border border-dashed border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-muted)] flex flex-col items-center justify-center gap-0.5 disabled:opacity-50"
                  data-testid="edit-ref-add"
                >
                  <L.Plus size={15}/>
                  <span className="text-[9px] uppercase tracking-widest">Add</span>
                </button>
              )}
              <input
                ref={refInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => { addRefs(e.target.files); e.target.value = ""; }}
                data-testid="edit-ref-input"
              />
            </div>
            {refs.length > 0 && (
              <div className="mt-2 text-[11px] text-[var(--rammy-text-muted)]">
                Say what to do with them — &ldquo;put this jacket on me&rdquo;, &ldquo;match this style&rdquo;.
              </div>
            )}
          </div>

          {/* Suggestions */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] mb-2 flex items-center gap-2">
              <L.Sparkles size={11} className="text-[var(--rammy-cyan-dark)]"/>
              Rammy&rsquo;s ideas
            </div>
            {loadingSuggest ? (
              <div className="flex gap-2">
                {[0,1,2].map(i => (
                  <div key={i} className="h-16 flex-1 rounded-xl bg-[var(--rammy-surface-alt)]/60 animate-pulse"/>
                ))}
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-[13px] text-[var(--rammy-text-muted)] italic">
                No ideas surfaced this time — describe your change below.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pickChip(i)}
                    disabled={busy}
                    className={`text-left rounded-xl border p-3 transition
                      ${activeChip === i
                        ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10"
                        : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)]/60"}
                      disabled:opacity-50`}
                    data-testid={`edit-suggestion-${i}`}
                  >
                    <div className="text-[13px] font-medium leading-tight">{s.label}</div>
                    <div className="mt-1 text-[11px] text-[var(--rammy-text-muted)] line-clamp-2">
                      {s.prompt}
                    </div>
                    {s.grounded_in && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">
                        <L.Brain size={10}/> from your memory
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Prompt */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] mb-2">
              What should change?
            </div>
            <textarea
              ref={textareaRef}
              rows={3}
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); setActiveChip(-1); }}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
              placeholder="e.g. Turn it into a soft watercolor. Or: replace the sky with pre-dawn purples."
              disabled={busy}
              className="w-full resize-none rounded-2xl border border-[var(--rammy-border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rammy-cyan)] focus:ring-2 focus:ring-[var(--rammy-cyan)]/30"
              data-testid="edit-prompt"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[var(--rammy-border)] flex items-center justify-between gap-3 bg-[var(--rm-surface)]">
          <div className="text-[11px] text-[var(--rammy-text-muted)]">
            Takes ~20 seconds · Result appears in the chat.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => !busy && onClose?.()}
              className="text-sm px-3 py-2 rounded-full text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)]"
              disabled={busy}
              data-testid="edit-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!prompt.trim() || busy}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[var(--rammy-cyan)] text-[var(--rammy-text)] text-sm font-medium hover:bg-[var(--rammy-cyan-dark)] hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="edit-submit"
            >
              {busy
                ? <><L.Loader2 size={14} className="animate-spin"/> Editing…</>
                : <>Apply edit <span className="text-[11px] opacity-70">⌘↵</span></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
